import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useState
} from 'react'
import { AttackEventForExclusion, ExclusionFormData } from './shared'
import { useRules } from '../rules/api/use-rules'
import {
  AgentLanguage,
  AgentLanguageValue
} from '../agent-language/agent-language'
import { GroupedRule } from './rule-group'
import { BaseProtectRule } from '../protect-rules/shared'
import { BaseAssessRule } from '../assess-rules'
import {
  getAssessOnlyRules,
  getCommonRules,
  getProtectOnlyRules
} from './rules-helper'

export interface ExclusionErrorForm {
  exclusionName?: string
  codes?: string
  urls?: string
  queues?: string
  inputName?: string
}

export interface ChildrenProps {
  exclusion: ExclusionFormData
  exclusionError: ExclusionErrorForm
  shouldSuppressEvents: boolean
  selectedRules: GroupedRule[]
  commonRules: BaseProtectRule[]
  assessOnlyRules: BaseAssessRule[]
  protectOnlyRules: BaseProtectRule[]
  isLoadingRules: boolean
  setExclusion: Dispatch<SetStateAction<ExclusionFormData>>
  setExclusionError: Dispatch<SetStateAction<ExclusionErrorForm>>
  setSelectedRules: Dispatch<SetStateAction<GroupedRule[]>>
  toggleShouldSuppressEvents: () => void
}

interface Props {
  exclusion: ExclusionFormData
  exclusionRules?: {
    selectedAssesRules: BaseAssessRule[]
    selectedProtectRules: BaseProtectRule[]
  }
  organizationUuid: string
  language: AgentLanguageValue
  attackEvent?: AttackEventForExclusion
  shouldAllowSuppressEvents?: boolean
  children: (props: ChildrenProps) => ReactElement
  isAssessFeatureEnabled: boolean
  isProtectFeatureEnabled: boolean
}

export function ExclusionFormManager({
  exclusion: initExclusion,
  exclusionRules,
  organizationUuid,
  language,
  attackEvent,
  shouldAllowSuppressEvents = false,
  children,
  isAssessFeatureEnabled,
  isProtectFeatureEnabled
}: Props) {
  const [exclusion, setExclusion] = useState<ExclusionFormData>(initExclusion)
  const [exclusionError, setExclusionError] = useState<ExclusionErrorForm>({
    urls: undefined,
    codes: undefined,
    exclusionName: undefined,
    queues: undefined,
    inputName: undefined
  })
  const [shouldSuppressEvents, setShouldSuppressEvents] = useState(
    shouldAllowSuppressEvents && !!attackEvent
  )
  const [selectedRules, setSelectedRules] = useState<GroupedRule[]>([])

  const {
    rules,
    isError: isErrorLoadingRules,
    isLoading: isLoadingRules
  } = useRules({
    organizationUuid,
    language: AgentLanguage.fromValue(language).getName(),
    exclusionType: exclusion.exclusionType,
    shouldLoadAssess: isAssessFeatureEnabled,
    shouldLoadProtect: isProtectFeatureEnabled
  })

  if (isErrorLoadingRules) {
    throw new Error('Failed getting rules data')
  }

  const allAssessRules = rules?.assessRules || []
  const allProtectRules = rules?.protectRules || []

  const commonRules = getCommonRules(allAssessRules, allProtectRules)
  const assessOnlyRules = getAssessOnlyRules(commonRules, allAssessRules)
  const protectOnlyRules = getProtectOnlyRules(commonRules, allProtectRules)

  useEffect(() => {
    if (attackEvent && allProtectRules.length > 0) {
      const selectedRule = findSelectedRuleMatch(
        attackEvent.ruleUuid,
        allProtectRules,
        commonRules
      )

      if (selectedRule) {
        setSelectedRules([selectedRule])
      }
    } else if (exclusionRules) {
      const { selectedAssesRules, selectedProtectRules } = exclusionRules

      setSelectedRules(
        findSelectedRulesMatches(
          selectedAssesRules,
          selectedProtectRules,
          assessOnlyRules,
          protectOnlyRules,
          commonRules
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules])

  return children({
    exclusion,
    exclusionError,
    shouldSuppressEvents,
    selectedRules,
    commonRules,
    assessOnlyRules,
    protectOnlyRules,
    isLoadingRules,
    setExclusion,
    setExclusionError,
    setSelectedRules,
    toggleShouldSuppressEvents: () =>
      setShouldSuppressEvents(!shouldSuppressEvents)
  })
}

export function findSelectedRuleMatch(
  attackEventRuleUuid: string,
  allProtectRules: BaseProtectRule[],
  commonRules: BaseProtectRule[]
): GroupedRule | undefined {
  const rule = allProtectRules.find(
    ({ uuid: ruleUuid }) => ruleUuid === attackEventRuleUuid
  )

  if (rule) {
    const isCommonRule = !!commonRules.find(({ uuid: ruleUuid }) => {
      return ruleUuid === attackEventRuleUuid
    })

    return {
      value: rule.uuid,
      name: rule.displayName,
      group: isCommonRule ? 'BOTH' : 'PROTECT',
      isAllOption: false
    }
  } else {
    return undefined
  }
}

export function findSelectedRulesMatches(
  selectedAssesRules: BaseAssessRule[],
  selectedProtectRules: BaseProtectRule[],
  assessOnlyRules: BaseAssessRule[],
  protectOnlyRules: BaseProtectRule[],
  commonRules: BaseProtectRule[]
): GroupedRule[] {
  const selectedCommonRules = getCommonRules(
    selectedAssesRules,
    selectedProtectRules
  )
  const selectedAssessOnlyRules = getAssessOnlyRules(
    selectedCommonRules,
    selectedAssesRules
  )
  const selectedProtectOnlyRules = getProtectOnlyRules(
    selectedCommonRules,
    selectedProtectRules
  )

  const commonGroupedRules: GroupedRule[] = []

  selectedCommonRules?.forEach((a) => {
    const ruleFound = commonRules.find((b) => b.uuid === a.uuid)

    if (ruleFound) {
      commonGroupedRules.push({
        value: ruleFound.uuid,
        name: ruleFound.displayName,
        group: 'BOTH',
        isAllOption: false
      })
    }
  })

  const protectGroupedRules: GroupedRule[] = []

  selectedProtectOnlyRules?.forEach((a) => {
    const ruleFound = protectOnlyRules.find((b) => b.uuid === a.uuid)

    if (ruleFound) {
      protectGroupedRules.push({
        value: ruleFound.uuid,
        name: ruleFound.displayName,
        group: 'PROTECT',
        isAllOption: false
      })
    }
  })

  const assessGroupedRules: GroupedRule[] = []

  selectedAssessOnlyRules?.forEach((a) => {
    const ruleFound = assessOnlyRules.find((b) => b.name === a.name)

    if (ruleFound) {
      assessGroupedRules.push({
        value: ruleFound.name,
        name: ruleFound.title,
        group: 'ASSESS',
        isAllOption: false
      })
    }
  })

  return [...commonGroupedRules, ...protectGroupedRules, ...assessGroupedRules]
}
